/**
 * WaPollCard — render Polaris de una encuesta inbound de WhatsApp.
 *
 * READ-ONLY a propósito: los votos (pollUpdateMessage) y la capacidad de
 * votar llegan/se envían cifrados E2E (secretEncryptedMessage). Evolution/
 * Baileys NO los descifra → es imposible mostrar conteos o votar desde
 * Smarty (WhatsApp Web sí puede porque ES el cliente oficial con las
 * claves E2E). Mostramos pregunta + opciones de forma clara y honesta.
 *
 * El backend formatea el body como:
 *   "📊 *Encuesta*\n{pregunta}\n\n▢ {opción1}\n▢ {opción2}"
 * (formato propio, estable). Parseamos ese formato — no payload arbitrario.
 */

import { Box, BlockStack, InlineStack, Text } from '@/lib/polaris';

const POLL_PREFIX = '📊 *Encuesta*';

export function isPollBody(body: string | null | undefined): boolean {
  return !!body && body.startsWith(POLL_PREFIX);
}

interface ParsedPoll {
  question: string;
  options: string[];
}

function parsePoll(body: string): ParsedPoll {
  const lines = body.split('\n');
  // lines[0] = "📊 *Encuesta*"; lines[1] = pregunta; resto = "▢ opción"
  const question = (lines[1] ?? '').trim();
  const options = lines
    .slice(2)
    .map((l) => l.trim())
    .filter((l) => l.startsWith('▢'))
    .map((l) => l.replace(/^▢\s*/, '').trim())
    .filter((l) => l.length > 0);
  return { question, options };
}

export function WaPollCard({ body }: { body: string }) {
  const { question, options } = parsePoll(body);

  return (
    <Box
      background="bg-surface-secondary"
      borderRadius="200"
      padding="300"
      minWidth="240px"
    >
      <BlockStack gap="200">
        <InlineStack gap="150" blockAlign="center">
          <Text as="span" variant="headingMd">📊</Text>
          <Text as="span" variant="headingSm" fontWeight="semibold">
            Encuesta
          </Text>
        </InlineStack>

        {question ? (
          <Text as="p" variant="bodyMd" fontWeight="medium">
            {question}
          </Text>
        ) : null}

        <BlockStack gap="150">
          {options.map((opt, i) => (
            <InlineStack key={`${i}-${opt}`} gap="200" blockAlign="center" wrap={false}>
              <span
                aria-hidden
                style={{
                  width: 16,
                  height: 16,
                  borderRadius: '50%',
                  border: '2px solid var(--p-color-border)',
                  flexShrink: 0,
                  display: 'inline-block',
                }}
              />
              <Text as="span" variant="bodyMd">{opt}</Text>
            </InlineStack>
          ))}
        </BlockStack>

        <Text as="p" variant="bodySm" tone="subdued">
          Los votos no están disponibles vía esta integración.
        </Text>
      </BlockStack>
    </Box>
  );
}
